import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  Users,
  Building2,
  Briefcase,
  CheckSquare,
  Calendar,
  CalendarClock,
  BookOpen,
  Car,
  TrendingUp,
  Clock,
  UserCircle,
  CornerDownLeft,
  Hash,
  ListTodo,
  Timer,
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

interface CommandItem {
  id: string;
  name: string;
  path: string;
  icon: React.ElementType;
  group: string;
  keywords: string[];
  roles?: ('Verwaltung' | 'Mitarbeiter')[];
}

const getCommands = (tNav: (key: string) => string, tHeader: (key: string) => string): CommandItem[] => [
  { id: 'dashboard', name: tNav('menu.dashboard'), path: '/', icon: LayoutDashboard, group: tHeader('groups.allgemein'), keywords: ['home', 'start', 'übersicht'] },
  { id: 'open-items', name: tNav('menu.openItems'), path: '/open-items', icon: ListTodo, group: tHeader('groups.allgemein'), keywords: ['offen', 'punkte', 'todo', 'erledigen', 'aufgabe'] },
  { id: 'planning', name: tNav('menu.planning'), path: '/planning', icon: Calendar, group: tHeader('groups.allgemein'), keywords: ['plan', 'woche', 'tag', 'schicht', 'dienst'] },
  { id: 'absences', name: tNav('menu.absences'), path: '/absences', icon: CalendarClock, group: tHeader('groups.allgemein'), keywords: ['urlaub', 'krank', 'frei', 'abwesend'] },
  { id: 'profile', name: tNav('menu.myProfile'), path: '/profile', icon: UserCircle, group: tHeader('groups.allgemein'), keywords: ['profil', 'konto', 'einstellungen', 'account'] },
  { id: 'customers', name: tNav('menu.customers'), path: '/customers', icon: Users, group: tHeader('groups.verwaltung'), keywords: ['kunde', 'client', 'firma'], roles: ['Verwaltung'] },
  { id: 'objects', name: tNav('menu.objects'), path: '/objects', icon: Building2, group: tHeader('groups.verwaltung'), keywords: ['gebäude', 'standort', 'objekt'], roles: ['Verwaltung'] },
  { id: 'employees', name: tNav('menu.employees'), path: '/employees', icon: Users, group: tHeader('groups.verwaltung'), keywords: ['personal', 'mitarbeiter', 'team'], roles: ['Verwaltung'] },
  { id: 'services', name: tNav('menu.services'), path: '/services', icon: Briefcase, group: tHeader('groups.verwaltung'), keywords: ['leistung', 'service', 'reinigung'], roles: ['Verwaltung'] },
  { id: 'tasks', name: tNav('menu.tasks'), path: '/tasks', icon: CheckSquare, group: tHeader('groups.verwaltung'), keywords: ['task', 'aufgabe', 'todo'], roles: ['Verwaltung'] },
  { id: 'price-indexes', name: tNav('menu.priceIndex'), path: '/price-indexes/history', icon: TrendingUp, group: tHeader('groups.verwaltung'), keywords: ['preis', 'index', 'historie'], roles: ['Verwaltung'] },
  { id: 'work-schedule-models', name: tNav('menu.workScheduleModels'), path: '/work-schedule-models', icon: Clock, group: tHeader('groups.verwaltung'), keywords: ['arbeitszeit', 'modell', 'stunden'], roles: ['Verwaltung'] },
  { id: 'vehicles', name: tNav('menu.vehicles'), path: '/vehicles', icon: Car, group: tHeader('groups.verwaltung'), keywords: ['fahrzeug', 'kennzeichen', 'auto'], roles: ['Verwaltung'] },
  { id: 'vehicleLogbook', name: tNav('menu.vehicleLogbook'), path: '/vehicles/logbook', icon: BookOpen, group: tHeader('groups.verwaltung'), keywords: ['fahrtenbuch', 'kilometer', 'km', 'fahrt'], roles: ['Verwaltung'] },
  { id: 'timeclock-groups', name: tNav('menu.timeClockGroups'), path: '/settings/timeclock-groups', icon: Timer, group: tHeader('groups.verwaltung'), keywords: ['stempel', 'stempelzeit', 'gruppe', 'winterdienst', 'einstempeln'], roles: ['Verwaltung'] },
  { id: 'absences-admin', name: tNav('menu.absencesAdmin'), path: '/absences/admin', icon: CalendarClock, group: tHeader('groups.verwaltung'), keywords: ['urlaub', 'krank', 'genehmigen', 'abwesenheit'], roles: ['Verwaltung'] },
];

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const { t } = useTranslation(['navigation', 'header']);
  const navigate = useNavigate();
  const { employee } = useAuthStore();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const isAdmin = employee?.role === 'Verwaltung' || employee?.role === 'SuperAdmin';

  const allCommands = useMemo(
    () => getCommands(t, (key: string) => t(`header:${key}`)),
    [t]
  );

  const availableCommands = useMemo(
    () => allCommands.filter((cmd) => !cmd.roles || isAdmin),
    [allCommands, isAdmin]
  );

  const filteredCommands = useMemo(() => {
    if (!query.trim()) return availableCommands;
    const q = query.toLowerCase();
    return availableCommands.filter(
      (cmd) =>
        cmd.name.toLowerCase().includes(q) ||
        cmd.group.toLowerCase().includes(q) ||
        cmd.keywords.some((kw) => kw.includes(q))
    );
  }, [query, availableCommands]);

  // Group filtered commands
  const groupedCommands = useMemo(() => {
    const groups: { label: string; items: CommandItem[] }[] = [];
    const groupMap = new Map<string, CommandItem[]>();

    for (const cmd of filteredCommands) {
      const existing = groupMap.get(cmd.group);
      if (existing) {
        existing.push(cmd);
      } else {
        groupMap.set(cmd.group, [cmd]);
      }
    }

    for (const [label, items] of groupMap) {
      groups.push({ label, items });
    }

    return groups;
  }, [filteredCommands]);

  // Flat index for keyboard navigation
  const flatItems = useMemo(() => filteredCommands, [filteredCommands]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Animation
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      requestAnimationFrame(() => setIsVisible(true));
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const executeCommand = useCallback(
    (cmd: CommandItem) => {
      onClose();
      navigate(cmd.path);
    },
    [navigate, onClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % flatItems.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
      } else if (e.key === 'Enter' && flatItems[selectedIndex]) {
        e.preventDefault();
        executeCommand(flatItems[selectedIndex]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [flatItems, selectedIndex, executeCommand, onClose]
  );

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${selectedIndex}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  let globalIndex = -1;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] sm:pt-[18vh]">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-slate-900/40 transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Palette */}
      <div
        className={`relative w-full max-w-[560px] mx-4 bg-white rounded-xl border border-slate-200 overflow-hidden transition-all duration-200 ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-[0.98] -translate-y-2'
        }`}
        style={{ boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)' }}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <Search className="w-[18px] h-[18px] text-slate-400 flex-shrink-0" strokeWidth={2} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('header:menu.searchPlaceholder')}
            className="flex-1 bg-transparent text-[15px] text-slate-900 placeholder:text-slate-400 outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[11px] font-medium text-slate-500 bg-slate-100 rounded-md border border-slate-200">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[360px] overflow-y-auto overscroll-contain">
          {filteredCommands.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Search className="w-5 h-5 text-slate-400" strokeWidth={1.75} />
              </div>
              <p className="text-[13px] font-medium text-slate-500">
                {t('header:menu.noResults', { query })}
              </p>
            </div>
          ) : (
            groupedCommands.map((group) => (
              <div key={group.label}>
                {/* Group Label */}
                <div className="px-5 pt-3 pb-1.5 flex items-center gap-2">
                  <Hash className="w-3 h-3 text-slate-400" strokeWidth={2} />
                  <span className="text-[10px] font-semibold font-mono uppercase tracking-[0.18em] text-slate-400">
                    {group.label}
                  </span>
                </div>

                {/* Items */}
                <div className="px-2 pb-1">
                  {group.items.map((cmd) => {
                    globalIndex++;
                    const idx = globalIndex;
                    const Icon = cmd.icon;
                    const isSelected = idx === selectedIndex;
                    return (
                      <button
                        key={cmd.id}
                        data-index={idx}
                        onClick={() => executeCommand(cmd)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors duration-100 ${
                          isSelected ? 'bg-emerald-50' : ''
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 transition-colors duration-100 ${
                            isSelected
                              ? 'bg-[#00A871] text-white'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          <Icon className="w-[15px] h-[15px]" strokeWidth={2} />
                        </div>
                        <span
                          className={`text-[13px] font-medium flex-1 transition-colors duration-100 ${
                            isSelected ? 'text-slate-900' : 'text-slate-700'
                          }`}
                        >
                          {cmd.name}
                        </span>
                        <span
                          className={`text-[11px] tabular-nums transition-opacity duration-100 ${
                            isSelected ? 'opacity-100 text-slate-500' : 'opacity-0'
                          }`}
                        >
                          {cmd.path}
                        </span>
                        <CornerDownLeft
                          className={`w-3.5 h-3.5 flex-shrink-0 transition-opacity duration-100 ${
                            isSelected ? 'opacity-100 text-[#00A871]' : 'opacity-0'
                          }`}
                          strokeWidth={2}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-5 py-2.5 border-t border-slate-100 text-[11px] text-slate-500">
          <span className="flex items-center gap-1.5">
            <kbd className="inline-flex items-center justify-center w-5 h-5 bg-slate-100 rounded border border-slate-200 font-medium text-[10px]">↑</kbd>
            <kbd className="inline-flex items-center justify-center w-5 h-5 bg-slate-100 rounded border border-slate-200 font-medium text-[10px]">↓</kbd>
            <span className="ml-0.5">{t('header:menu.hintNavigate')}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="inline-flex items-center justify-center h-5 px-1.5 bg-slate-100 rounded border border-slate-200 font-medium text-[10px]">↵</kbd>
            <span className="ml-0.5">{t('header:menu.hintOpen')}</span>
          </span>
          <span className="flex items-center gap-1.5 ml-auto">
            <kbd className="inline-flex items-center justify-center h-5 px-1.5 bg-slate-100 rounded border border-slate-200 font-medium text-[10px]">{t('header:menu.hintCtrl')}</kbd>
            <span>+</span>
            <kbd className="inline-flex items-center justify-center w-5 h-5 bg-slate-100 rounded border border-slate-200 font-medium text-[10px]">K</kbd>
          </span>
        </div>
      </div>
    </div>
  );
}
